import { inject, Injectable } from '@angular/core';

import { Actions } from '@ngrx/effects';
import { catchError, mergeMap, of, switchMap } from 'rxjs';

import { createEffectFactory } from '@core/ngrx-legacy';

import { TellentSsoHttpClient } from './tellent-sso-http-client.service';
import { TellentSsoActions } from './tellent-sso.store';

@Injectable({ providedIn: 'root' })
export class TellentSsoEffects {
  public actions$ = inject(Actions);
  public http = inject(TellentSsoHttpClient);
  public effects = createEffectFactory(TellentSsoActions, this.actions$);

  public fetch$ = this.effects(TellentSsoActions.fetch, () => this.http.fetch());

  public uploadXml$ = this.effects(TellentSsoActions.uploadXml, ({ payload }) =>
    this.http.uploadMetadataXml(payload.file, payload.entityId).pipe(
      mergeMap(payload => [
        TellentSsoActions.fetchSuccess({ payload }),
        TellentSsoActions.uploadXmlSuccess({ payload }),
      ]),
      catchError(err =>
        of(
          TellentSsoActions.uploadXmlFailure({
            payload: { error: err?.error?.errors.metadataXml[0] },
          }),
        ),
      ),
    ),
  );

  public replaceXml$ = this.effects(TellentSsoActions.replaceXml, ({ payload }) =>
    this.http.replaceMetadataXml(payload).pipe(
      mergeMap(payload => [
        TellentSsoActions.fetchSuccess({ payload }),
        TellentSsoActions.replaceXmlSuccess({ payload }),
      ]),
      catchError(err =>
        of(
          TellentSsoActions.replaceXmlFailure({
            payload: { error: err?.error?.errors.metadataXml[0] },
          }),
        ),
      ),
    ),
  );

  public updateRoles$ = this.effects(TellentSsoActions.updateRoles, ({ payload }) =>
    this.http.updateRoles(payload.roles, payload.entityId).pipe(
      mergeMap(({ defaultSeats }) => [
        TellentSsoActions.fetch(),
        TellentSsoActions.updateRolesSuccess({
          payload: {
            defaultSeats,
          },
        }),
      ]),
      catchError(err => of(TellentSsoActions.updateRolesFailure(err))),
    ),
  );

  public changeMode$ = this.effects(TellentSsoActions.changeMode, ({ payload }) =>
    this.http.changeMode(payload).pipe(
      mergeMap(payload => [
        TellentSsoActions.fetchSuccess({ payload }),
        TellentSsoActions.changeModeSuccess(),
      ]),
      catchError(err => of(TellentSsoActions.changeModeFailure(err))),
    ),
  );

  public changeSessionLength$ = this.effects(TellentSsoActions.changeSessionLength, ({ payload }) =>
    this.http.changeSessionLength(payload).pipe(
      mergeMap(payload => [
        TellentSsoActions.fetchSuccess({ payload }),
        TellentSsoActions.changeSessionLengthSuccess(),
      ]),
      catchError(err => of(TellentSsoActions.changeSessionLengthFailure(err))),
    ),
  );

  public delete$ = this.effects(TellentSsoActions.delete, ({ payload }) =>
    this.http.delete(payload).pipe(
      switchMap(() => [TellentSsoActions.fetch(), TellentSsoActions.deleteSuccess()]),
      catchError(err => of(TellentSsoActions.deleteFailure({ payload: err }))),
    ),
  );
}
